import { useCallback, useEffect, useRef } from 'react';
import { Trans } from 'react-i18next';

import { Alert } from '@zooplus/ui-components';

import BraintreeCreditCardForm from '@components/BraintreeCreditCardForm/BraintreeCreditCardForm';
import StoredCreditCards from '@components/StoredCreditCards/StoredCreditCards';
import useEmergencyState from '@hooks/useEmergencyState';
import { useTracking } from '@hooks/useTracking';
import { useTranslation } from '@hooks/useTranslations';
import { useCreditcardService } from '@providers/CreditCardsMutationProvider';
import { useErrorContext } from '@providers/ErrorContextProvider';
import { captureException } from '@services/sentryService';
import { ContainerType, CustomerType } from '@types-internal';
import {
  BraintreeCreditCardFormNewHandle as BraintreeCreditCardFormHandle,
  TokenizeCardResult,
  TokenizeCardResultData,
} from '@types-internal/BraintreeTypes';

export type BraintreePaymentMethodProps = {
  containerType: ContainerType;
  token: string;
  isFallbackPsp: boolean;
  customerType: CustomerType;
  currency?: string;
  customerId?: string;
  sid?: string;
  siteId?: number;
};

type TokenizeResult = {
  redirect: boolean;
  data?: TokenizeCardResultData;
};

const BraintreePaymentMethod: React.FC<BraintreePaymentMethodProps> = ({
  containerType,
  customerType,
  token,
  isFallbackPsp,
  currency,
  customerId,
  sid,
  siteId,
}) => {
  const { t } = useTranslation();
  const formHandle = useRef<BraintreeCreditCardFormHandle>(null);
  const { error, setError } = useErrorContext();
  const {
    data: { creditCards },
  } = useCreditcardService();
  const track = useTracking({
    sid,
    siteId,
    currency,
    customerId,
    guest: customerType === 'GUEST_CUSTOMER',
  });

  // Callback for shop-checkout
  const braintreeTokenizeCard = useCallback(async (): Promise<TokenizeResult> => {
    if (error && !error?.retryable) {
      throw new Error(error.message);
    } else if (!formHandle.current) {
      return { redirect: true };
    } else {
      let tokenizationResult: TokenizeCardResult;
      try {
        tokenizationResult = await formHandle.current?.tokenizeCard();
      } catch (error: any) {
        track?.('BTformKO');
        setError({
          message: t('checkout.payment.creditcard.error.tokenization.retry'),
          retryable: false,
        });
        throw captureException({ message: 'braintree_tokenization_failed' });
      }
      if (tokenizationResult.success) {
        track?.('BTformOK');
        return { redirect: false, data: tokenizationResult.data };
      } else {
        track?.('BTformKO');
        if (tokenizationResult.errorDisplayable) {
          setError({
            message: t('checkout.payment.creditcard.error.tokenization.retry'),
            retryable: true,
          });
        }
        throw new Error('braintree_tokenization_failed');
      }
    }
  }, [t, error, setError]);

  // For calling from parent page
  useEffect(() => {
    (window as any).Payment = {
      ...((window as any).Payment || {}),
      braintreeTokenizeCard,
    };
    return () => {
      (window as any).Payment.braintreeTokenizeCard = undefined;
    };
  }, [braintreeTokenizeCard]);

  const displayPossible = !error || error?.retryable;
  const isMyAccount = containerType === 'myaccount';
  const isGuest = customerType === 'GUEST_CUSTOMER';

  useEmergencyState(isFallbackPsp, isMyAccount, t, setError);

  return (
    <>
      {displayPossible && (!!creditCards?.length || isMyAccount) && (
        <StoredCreditCards isMyAccount={isMyAccount} isFallbackPsp={isFallbackPsp} />
      )}
      {displayPossible && !creditCards?.length && !isMyAccount && (
        <>
          <Alert>
            <Trans
              t={t}
              i18nKey="checkout.payment.creditcard.3d.secure.message"
              components={{ url: <a target="_blank" href="/info/about/psd2_summary" /> }}
            />
          </Alert>
          <BraintreeCreditCardForm
            token={token}
            ref={formHandle}
            containerType={containerType}
            labelsInline={containerType !== 'registration'}
            allowVaulting={!isFallbackPsp && !isGuest}
            currency={currency}
            customerId={customerId}
            customerType={customerType}
            sid={sid}
            siteId={siteId}
          />
        </>
      )}
    </>
  );
};

export default BraintreePaymentMethod;
